// orgPackUpSubmit.js — Main orchestrator. Validates → Transforms → Uploads → Submits.

import { toast } from 'react-toastify';
import errorHandle from '../../../../utils/errorHandle';
import {
    validateBasicFields,
    validateQuickView,
    validateItinerary,
    validateImages,
    validateTextBlocks,
    validateFAQs,
    validatePrices,
    validateInstallments,
    validateTags,
    validateBasicFieldsOthers,
    validateTimeSlots,
    validatePricesOthers
} from '../../../../utils/packUploads/uploadValidators';
import { uploadImages, uploadVideo } from '../../../../utils/packUploads/mediaUploads';
import { normalizePriceFields, splitTextBlocks } from '../../../../utils/packUploads/transformers';
import { actCreateFun, orgActiveActEdit, orgInactiveActEdit } from '../../../../api/orgActAPI';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Runs a validator, shows a toast on failure, and optionally scrolls to the
 * offending field. Returns false if validation failed (caller should bail out).
 */
const check = (result, ref, scrollArg) => {
    if (!result.valid) {
        toast.error(result.message);
        ref?.current?.scrollToAndHighlightField(result.field ?? scrollArg ?? '');
        return false;
    }
    return true;
};

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export default async function orgActUpSubmit(packData, tempData, imgData, setLoading, isEdit, videoFile, refs = {}) {
    // ── 1. Validation ────────────────────────────────────────────────────────

    if (!check(validateBasicFieldsOthers(packData), refs.basicRef)) return;
    if (!check(validateQuickView(packData.quickView), refs.quickViewRef)) return;
    if (!check(validateTimeSlots(packData), refs.timeSlotRef)) return;
    if (!check(validateItinerary(packData), refs.itineraryRef)) return;
    if (!check(validateImages(imgData), refs.imageRef)) return;
    if (!check(validateTextBlocks(tempData), getTextRef(tempData, refs))) return;

    // Normalize price arrays before price validation
    packData = { ...packData, price: normalizePriceFields(packData) };

    if (!check(validateFAQs(packData.FAQ), refs.faqRef)) return;
    if (!check(validatePricesOthers(packData), refs.priceRef, 'price')) return;
    //if (!check(validateInstallments(packData.price))) return;
    if (!check(validateTags(packData.tags), refs.tagsRef)) return;

    // ── 2. Build submission payload ──────────────────────────────────────────

    setLoading(true);

    try {
        const type = localStorage.getItem('type');
        const { images, cardImages } = await uploadImages(imgData, type, isEdit);
        const videos = await uploadVideo(videoFile, packData, type, isEdit);

        const data = {
            ...packData,
            ...splitTextBlocks(tempData),
            organizerId: localStorage.getItem('orgId'),
            organizerName: localStorage.getItem('orgName'),
            images,
            cardImages,
            videos
        };

        // ── 3. API call ──────────────────────────────────────────────────────

        const response = await submitToAPI(data, isEdit);

        if (response.status === 200) {
            toast.success(`Package ${isEdit ? 'updated' : 'created'} successfully`);
            return response.status;
        }
    } catch (error) {
        errorHandle(error);
    } finally {
        setLoading(false);
    }
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

/**
 * Routes to the correct API function depending on edit vs. create
 * and (for edits) active vs. inactive trip status.
 */
async function submitToAPI(data, isEdit) {
    if (!isEdit) {
        const { _id, ...rest } = data;
        return actCreateFun(rest);
    }

    if (data.status === 'active') {
        // Active trips: strip fields that aren't allowed to change while live
        const { status, title, overview, days, nights, itinerary, included, excluded, ...rest } = data;
        return orgActiveActEdit(data._id, rest);
    }

    return orgInactiveActEdit(data._id, data);
}

/**
 * Returns the correct ref for the first failing text block field
 * so the scroll-to logic works across different section refs.
 */
function getTextRef(tempData, refs) {
    const fieldRefMap = {
        included: refs.inExRef,
        excluded: refs.inExRef,
        safety: refs.safetyRef,
        carry: refs.thingsRef,
        terms: refs.termsRef
    };

    // Return a proxy that exposes the right ref for whichever field fails
    return {
        current: {
            scrollToAndHighlightField: (field) => {
                const ref = fieldRefMap[field];
                ref?.current?.scrollToAndHighlightField(field);
            }
        }
    };
}
